import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Global date formatting utilities
 * All dates should be displayed in MM-DD-YYYY format
 * All times should be displayed in AM/PM format
 */

/**
 * Formats a Date object to MM-DD-YYYY format
 * @param date - Date object to format
 * @returns Formatted date string (MM-DD-YYYY)
 */
export function formatDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
}

/**
 * Formats a Date object to AM/PM time format
 * @param date - Date object to format
 * @returns Formatted time string (HH:MM AM/PM)
 */
export function formatTime(date: Date): string {
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  const formattedHours = String(hours).padStart(2, '0');
  return `${formattedHours}:${minutes} ${ampm}`;
}

/**
 * Formats a Date object to both date and time
 * @param date - Date object to format
 * @returns Object with formatted date and time
 */
export function formatDateTime(date: Date): { date: string; time: string } {
  return {
    date: formatDate(date),
    time: formatTime(date)
  };
}

/**
 * Formats a date string or Date object to MM-DD-YYYY HH:MM AM/PM format
 * @param dateInput - Date string or Date object to format
 * @returns Formatted date and time string
 */
export function formatDateTimeString(dateInput: string | Date): string {
  let date: Date | null = null;

  if (typeof dateInput === 'string') {
    // Use parseDateString to handle various date formats
    date = parseDateString(dateInput);
    // If parseDateString fails, try standard Date parsing
    if (!date) {
      date = new Date(dateInput);
    }
  } else {
    date = dateInput;
  }

  // Check if the date is valid
  if (!date || isNaN(date.getTime())) {
    console.warn('Invalid date provided to formatDateTimeString:', dateInput);
    return typeof dateInput === 'string' ? dateInput : 'Invalid Date';
  }

  const { date: formattedDate, time: formattedTime } = formatDateTime(date);
  return `${formattedDate} ${formattedTime}`;
}

/**
 * Formats a date string or Date object to MM-DD-YYYY format only
 * @param dateInput - Date string or Date object to format
 * @returns Formatted date string
 */
export function formatDateString(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  // Check if the date is valid
  if (!date || isNaN(date.getTime())) {
    console.warn('Invalid date provided to formatDateString:', dateInput);
    return 'Invalid Date';
  }

  return formatDate(date);
}

/**
 * Formats a date string or Date object to HH:MM AM/PM format only
 * @param dateInput - Date string or Date object to format
 * @returns Formatted time string
 */
export function formatTimeString(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  // Check if the date is valid
  if (!date || isNaN(date.getTime())) {
    console.warn('Invalid date provided to formatTimeString:', dateInput);
    return 'Invalid Time';
  }

  return formatTime(date);
}

/**
 * Parses various date string formats and returns a Date object
 * Handles formats like: "09/30/2025, 10:51 AM", "2025-09-30T10:51:00Z", "09-30-2025", etc.
 * @param dateString - Date string in various formats
 * @returns Date object or null if invalid
 */
export function parseDateString(dateString: string): Date | null {
  if (!dateString) return null;

  // Handle format like "09/30/2025, 10:51 AM"
  if (dateString.includes(', ')) {
    const [datePart, timePart] = dateString.split(', ');
    const date = new Date(`${datePart} ${timePart}`);
    return isNaN(date.getTime()) ? null : date;
  }

  // Handle format like "09-03-2025 / 09:30 AM"
  if (dateString.includes(' / ')) {
    const [datePart, timePart] = dateString.split(' / ');
    // Convert MM-DD-YYYY to MM/DD/YYYY for proper parsing
    const formattedDate = datePart.replace(/-/g, '/');
    const date = new Date(`${formattedDate} ${timePart}`);
    return isNaN(date.getTime()) ? null : date;
  }

  // Handle format like "12/25/2024 10:30 AM" (API format)
  if (/^\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}\s+(AM|PM)$/i.test(dateString)) {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

  // Handle ISO format and other standard formats
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Formats a date string that might be in various formats to MM-DD-YYYY HH:MM AM/PM
 * @param dateString - Date string in any format
 * @returns Formatted date and time string
 */
export function formatAnyDateString(dateString: string): string {
  const date = parseDateString(dateString);
  if (!date) {
    console.warn('Could not parse date string:', dateString);
    return 'Invalid Date';
  }

  return formatDateTimeString(date);
}


/**
 * Generates a tag from a name by converting to lowercase and replacing spaces/special chars with underscores
 * @param name - The name to convert to a tag
 * @returns Generated tag string
 * 
 * Examples:
 * - "Washing" -> "washing"
 * - "Collision Repair" -> "collision_repair"
 * - "AC & Heating" -> "ac_heating"
 * - "24/7 Emergency" -> "24_7_emergency"
 */
export function generateTagFromName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    // Replace spaces, hyphens, and other special characters with underscores
    .replace(/[\s\-&/\\]+/g, '_')
    // Remove any characters that aren't letters, numbers, or underscores
    .replace(/[^a-z0-9_]/g, '')
    // Remove multiple consecutive underscores
    .replace(/_+/g, '_')
    // Remove leading/trailing underscores
    .replace(/^_+|_+$/g, '');
}

/**
 * Formats a status string for display
 * Replaces underscores/hyphens with spaces and title-cases the string
 * Example: "not_required" -> "Not Required", "payment-pending" -> "Payment Pending"
 * @param status - The raw status string
 * @returns Formatted status string
 */
export function formatStatus(status: string | null | undefined): string {
  if (!status) return '';

  return status
    // Replace underscores and hyphens with spaces
    .replace(/[_-]/g, ' ')
    // Split into words
    .split(' ')
    // Capitalize first letter of each word
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    // Join back with spaces
    .join(' ');
}

